import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  Check,
  ChevronDown,
  Home,
  MapPin,
  Minus,
  Plus,
  RefreshCw,
  ShoppingBag,
  Trash2,
  X,
} from "lucide-react";
import PaymentQr from "./PaymentQr";
import useCoupon from "../Services/useCoupon";
import { useLocation, useNavigate } from "react-router-dom";
import useOrders from "../Services/useOrders";

import { GlobalContext } from "../Context/GlobalContext";
// import useOrderWebSocket from "../Services/useOrderWebSocket";

import { useCart as useLocalCart } from "../Context/CartContext";
import NoImg from "../Images/Item_Image_Placeholder.webp";
import useCartService from "../Services/useCart";
import AddressModal from "./AddressModal";
import useAddress from "../Services/useAddress";

const CHECKOUT_RESULT_KEY = "chaafo-checkout-api-result";

const CHECKOUT_SIGNATURE_KEY = "chaafo-checkout-signature";

const checkoutRequestPromises = new Map();

/* HELPERS */

const formatCurrency = (value) => {
  const numberValue = Number(value || 0);

  return `₹${Number.isFinite(numberValue) ? numberValue.toFixed(0) : "0"}`;
};

const getNumber = (value, fallbackValue = 0) => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : fallbackValue;
};

const normalizeApiOrderType = (orderType) => {
  const normalizedValue = String(orderType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (normalizedValue === "homedelivery" || normalizedValue === "delivery") {
    return "home_delivery";
  }

  if (normalizedValue === "dinein" || normalizedValue === "dining") {
    return "dinein";
  }

  return "takeaway";
};

const getCustomerMobile = (user) => {
  return String(
    user?.number ||
      user?.mobile ||
      user?.phone ||
      user?.phone_no ||
      user?.mobile_number ||
      user?.customer_mobile ||
      "",
  ).trim();
};

const getCustomerName = (user) => {
  const firstName = String(
    user?.first_name ||
      user?.firstName ||
      sessionStorage.getItem("first_name") ||
      "",
  ).trim();

  const lastName = String(
    user?.last_name ||
      user?.lastName ||
      sessionStorage.getItem("last_name") ||
      "",
  ).trim();

  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return (
    fullName ||
    String(
      user?.name ||
        user?.customer_name ||
        sessionStorage.getItem("customer_name") ||
        "",
    ).trim()
  );
};

const getRestaurantUserId = (restaurant) => {
  return String(
    restaurant?.userid ||
      restaurant?.user_id ||
      restaurant?.shop_code ||
      restaurant?.restaurant_userid ||
      restaurant?.restaurantUserId ||
      "",
  ).trim();
};

const getAddressText = () => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const selectedAddress = String(
      localStorage.getItem("selectedDeliveryAddress") ||
        sessionStorage.getItem("selectedDeliveryAddress") ||
        "",
    ).trim();

    const addressSource = String(
        sessionStorage.getItem("selectedDeliveryAddressSource") ||
        "",
    )
      .trim()
      .toLowerCase();

    // =====================================================
    // NAVBAR AUTOMATIC GPS LOCATION
    // Checkout me nahi dikhani
    // =====================================================

    if (addressSource === "navbar-current-location") {
      return "";
    }

    // =====================================================
    // SAVED / MANUALLY SELECTED ADDRESS
    // =====================================================

    if (selectedAddress) {
      return selectedAddress;
    }
  } catch (error) {
    console.error("Unable to read checkout delivery address:", error);
  }

  return "";
};

const createCheckoutSignature = ({
  restaurantUserId,
  customerMobile,
  orderType,
  items,
}) => {
  return JSON.stringify({
    restaurantUserId,
    customerMobile,
    orderType: normalizeApiOrderType(orderType),

    items: Array.isArray(items)
      ? items.map((item) => ({
          itemId: String(item?.itemId || item?.productId || ""),

          sizeId: String(item?.sizeId || item?.size || "Regular"),

          quantity: Number(item?.quantity || 0),

          price: Number(item?.unitPrice || item?.itemPrice || 0),
        }))
      : [],
  });
};

const getStoredCheckout = (signature) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedSignature = window.localStorage.getItem(CHECKOUT_SIGNATURE_KEY);

    const storedResult = window.localStorage.getItem(CHECKOUT_RESULT_KEY);

    if (storedSignature !== signature || !storedResult) {
      return null;
    }

    const parsedResult = JSON.parse(storedResult);

    if (
      !parsedResult ||
      typeof parsedResult !== "object" ||
      !parsedResult?.orderId
    ) {
      return null;
    }

    return parsedResult;
  } catch (error) {
    console.error("Unable to restore checkout:", error);

    return null;
  }
};

const saveCheckoutResult = (signature, checkoutData) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(CHECKOUT_SIGNATURE_KEY, signature);

    window.localStorage.setItem(
      CHECKOUT_RESULT_KEY,
      JSON.stringify(checkoutData),
    );
  } catch (error) {
    console.error("Unable to save checkout result:", error);
  }
};

function SectionCard({ title, action, children, className = "" }) {
  return (
    <section
      className={`rounded-[24px] border border-blue-100 bg-white p-4 shadow-[0_10px_28px_rgba(30,64,175,0.08)] sm:p-6 ${className}`}
    >
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-[14px] font-bold uppercase tracking-wide text-slate-700 sm:text-[15px]">
          {title}
        </h2>

        {action}
      </div>

      {children}
    </section>
  );
}

function QuantityControl({ item, increaseItem, decreaseItem }) {
  return (
    <div className="flex h-9 items-center rounded-full bg-blue-700 px-1 text-white shadow-sm">
      <button
        type="button"
        onClick={() => decreaseItem(item)}
        className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/10"
        aria-label={`Decrease ${item.name}`}
      >
        <Minus size={15} strokeWidth={2.5} />
      </button>

      <span className="min-w-7 text-center text-xs font-bold">
        {String(item.quantity).padStart(2, "0")}
      </span>

      <button
        type="button"
        onClick={() => increaseItem(item)}
        className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/10"
        aria-label={`Increase ${item.name}`}
      >
        <Plus size={15} strokeWidth={2.5} />
      </button>
    </div>
  );
}

function CartItem({ item, increaseItem, decreaseItem, removeItem }) {
  const [customizationOpen, setCustomizationOpen] = useState(false);

  const getCustomizationEntries = () => {
    let parsedCustomization = item?.customization || {};

    if (typeof parsedCustomization === "string") {
      try {
        parsedCustomization = JSON.parse(parsedCustomization || "{}");
      } catch {
        parsedCustomization = {};
      }
    }

    if (
      !parsedCustomization ||
      typeof parsedCustomization !== "object" ||
      Array.isArray(parsedCustomization)
    ) {
      return [];
    }

    return Object.entries(parsedCustomization).filter(
      ([, value]) => String(value || "").trim() !== "",
    );
  };

  const customizationEntries = getCustomizationEntries();
  const hasCustomization = customizationEntries.length > 0;

  const itemName = item?.name || item?.productName || "Menu item";
  const itemSize = item?.size || item?.sizeId || "Regular";

  return (
    <div className="grid grid-cols-[56px_minmax(0,1fr)] gap-3 border-b border-slate-100 py-4 last:border-b-0 sm:grid-cols-[68px_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
      {/* IMAGE */}
      <div className="h-14 w-14 overflow-hidden rounded-xl bg-slate-100 sm:h-[68px] sm:w-[68px]">
        <img
          src={item.image || item.itemImage || NoImg}
          alt={itemName}
          className="h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = NoImg;
          }}
        />
      </div>

      {/* ITEM DETAILS */}
      <div className="min-w-0">
        {/* ITEM NAME + CUSTOMIZED BADGE */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
          <p className="min-w-0 text-[14px] font-medium leading-5 text-slate-800 sm:text-[15px]">
            {itemName} ({itemSize})
          </p>

          {hasCustomization && (
            <div
              className="relative shrink-0"
              onMouseEnter={() => setCustomizationOpen(true)}
              onMouseLeave={() => setCustomizationOpen(false)}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setCustomizationOpen((previous) => !previous);
                }}
                className="
                  inline-flex
                  shrink-0
                  items-center
                  rounded-full
                  border
                  border-blue-200
                  bg-blue-50
                  px-2
                  py-[3px]
                  text-[8px]
                  font-semibold
                  leading-none
                  text-blue-700
                  transition
                  hover:bg-blue-100
                  sm:px-2.5
                  sm:py-1
                  sm:text-[11px]
                "
              >
                Customized
              </button>

              {/* CUSTOMIZATION POPUP */}
              {customizationOpen && (
                <div
                  className="
  absolute
  left-1/2
  top-[calc(100%+6px)]
  z-[100]

  w-[150px]
  max-w-[calc(100vw-24px)]

  -translate-x-1/2

  rounded-[12px]
  border
  border-slate-200
  bg-white
  p-3

  shadow-[0_10px_30px_rgba(15,23,42,0.18)]

  sm:left-0
  sm:w-auto
  sm:min-w-[190px]
  sm:max-w-[260px]
  sm:translate-x-0
"
                >
                  <p className="mb-2 text-[11px] font-bold text-slate-800">
                    Selected Customizations
                  </p>

                  <div className="space-y-1.5">
                    {customizationEntries.map(([key, value]) => (
                      <p
                        key={key}
                        className="text-[11px] leading-4 text-slate-500"
                      >
                        <span className="font-semibold text-slate-700">
                          {key}:
                        </span>{" "}
                        {String(value)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* PRICE */}
        <p className="mt-1 text-xs text-slate-500">
          {formatCurrency(item.unitPrice || item.itemPrice)}
        </p>

        {/* MOBILE QUANTITY */}
        <div className="mt-3 flex items-center justify-between gap-3 sm:hidden">
          <QuantityControl
            item={item}
            increaseItem={increaseItem}
            decreaseItem={decreaseItem}
          />

          <button
            type="button"
            onClick={() => removeItem(item)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-500"
            aria-label={`Remove ${itemName}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* DESKTOP QUANTITY */}
      <div className="hidden items-center gap-2 sm:flex">
        <QuantityControl
          item={item}
          increaseItem={increaseItem}
          decreaseItem={decreaseItem}
        />

        <button
          type="button"
          onClick={() => removeItem(item)}
          className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-500"
          aria-label={`Remove ${itemName}`}
        >
          <Trash2 size={17} />
        </button>
      </div>
    </div>
  );
}

function BillRow({ label, value, strong = false, positive = false }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 ${
        strong ? "text-base font-bold text-slate-900" : "text-sm text-slate-600"
      }`}
    >
      <span>{label}</span>

      <span
        className={
          positive
            ? "font-semibold text-emerald-600"
            : "font-semibold text-slate-800"
        }
      >
        {value}
      </span>
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();

  const {
    user,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    setSelectedDeliveryLocation,
  } = useContext(GlobalContext);
  const { getOrderReceiptDetails } = useOrders();

 const {
   items,
   restaurant,
   itemCount,
   subtotal,
   increaseItem,
   decreaseItem,
   removeItem,
   clearCart,
 } = useLocalCart();

  const {
    generateOrderNumber,
    addToCart: addItemsToServerCart,
    calculateDeliveryChargesAndDiscount,
    getCartDetails,
    getCatalogItemsSuggestions,
    resetCartService,

    placeOrderSequentially,
    placingOrder,
    placeOrderError,

    cartLoading,
    cartError,
    currentStep,
  } = useCartService();

const {
  getCouponCodes,
  applyCouponCode,
  calculateDeliveryChargesAndDiscount: calculateCouponTotal,
} = useCoupon();
  const { getCustomerList, getCustomerFavouriteAddress } = useAddress();

  const location = useLocation();

  const [addressLoading, setAddressLoading] = useState(false);

  const checkoutStartedRef = useRef(false);

  const [checkoutPreparing, setCheckoutPreparing] = useState(false);

  const [checkoutApiData, setCheckoutApiData] = useState(null);

  const [localCheckoutError, setLocalCheckoutError] = useState("");

  const [recalculating, setRecalculating] = useState(false);

  const [coupon, setCoupon] = useState("");

  const [address, setAddress] = useState(() => getAddressText(user));

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState(null);

  const [selectedAddressDetails, setSelectedAddressDetails] = useState(() => {
    const initialAddress = getAddressText(user);

    return initialAddress
      ? {
          address: initialAddress,
          favourite: "Home",
          isDefault: true,
        }
      : null;
  });

  const restaurantUserId = getRestaurantUserId(restaurant);

  const customerMobile = getCustomerMobile(user);

  const customerName = getCustomerName(user);

  const handleOpenSavedAddresses = () => {
    setLocalCheckoutError("");

    navigate("/account/address", {
      state: {
        fromCheckout: true,
        customerId,
        customerMobile,
        restaurantUserId,
      },
    });
  };

  // console.log("===== CHECKOUT VALUES =====");
  // console.log("restaurant =", restaurant);
  // console.log("user =", user);
  // console.log("restaurantUserId =", restaurantUserId);
  // console.log("customerMobile =", customerMobile);
  // console.log("customerName =", customerName);

  const customerId = String(
    user?.user_code || user?.customerId || user?.customer_id || "",
  ).trim();

//  const currentPaymentOrderId = String(paymentDetails?.orderId || "").trim();

//  const orderSocketUserId = currentPaymentOrderId;

//  const orderSocketRoomId = orderSocketUserId
//    ? `${orderSocketUserId}_order_update_master_v2`
//    : "";

// const handlePaymentMessage = useCallback(
//   async (messageData) => {
//     console.log("📩 Payment WebSocket Data:", messageData);

//     const message = String(
//       messageData?.Message || messageData?.message || "",
//     ).trim();

//     console.log("📩 Payment WebSocket Message:", message);

//     const match = message.match(/Payment Received For Order\s*#?\s*(\d+)/i);

//     if (!match) {
//       return;
//     }

//     const receivedOrderId = String(match[1]).trim();

//     console.log(
//       "🔎 Received Order:",
//       receivedOrderId,
//       "Current Order:",
//       currentPaymentOrderId,
//     );

//     if (receivedOrderId !== currentPaymentOrderId) {
//       console.log("⏭️ Different order, ignoring");
//       return;
//     }

//     console.log("✅ PAYMENT SUCCESS VIA WEBSOCKET");

//     try {
//       console.log("🚀 Calling Receipt API for:", receivedOrderId);

//       const receiptResponse = await getOrderReceiptDetails({
//         orderid: receivedOrderId,
//         platform: "WEB",
//       });

//       console.log("✅ RECEIPT API RESPONSE:", receiptResponse);

//       setPaymentModalOpen(false);

//       navigate(`/order-summary?orderid=${receivedOrderId}&download=1`);
//     } catch (error) {
//       console.error("❌ RECEIPT API FAILED:", error);
//     }
//   },
//   [currentPaymentOrderId, getOrderReceiptDetails, navigate],
// );


const startCheckoutProcess = useCallback(
  async ({ force = false } = {}) => {
    setLocalCheckoutError("");

    const finalOrderType = "home_delivery";

    const finalCheckoutSignature = createCheckoutSignature({
      restaurantUserId,
      customerMobile,
      orderType: finalOrderType,
      items,
    });

    if (!restaurantUserId) {
      const message = "Restaurant user ID is missing.";

      setLocalCheckoutError(message);

      return {
        success: false,
        message,
      };
    }

    if (!customerMobile) {
      const message = "Customer mobile number is missing. Please login again.";

      setLocalCheckoutError(message);

      return {
        success: false,
        message,
      };
    }

    if (!Array.isArray(items) || items.length === 0) {
      const message = "Your cart is empty.";

      setLocalCheckoutError(message);

      return {
        success: false,
        message,
      };
    }

    setCheckoutPreparing(true);

    try {
      if (!force) {
        const savedCheckout = getStoredCheckout(finalCheckoutSignature);

        if (savedCheckout) {
          setCheckoutApiData(savedCheckout);

          // Call coupons again even when checkout data comes from cache
          try {
            const couponAmount = Number(
              savedCheckout?.calculation?.payAmount ??
                savedCheckout?.calculation?.orderAmount ??
                subtotal ??
                0,
            );

            const couponResult = await getCouponCodes({
              amount: couponAmount,
              orderId: savedCheckout.orderId,
              orderType: finalOrderType,
              userid: restaurantUserId,
            });

            setCheckoutApiData((previous) => ({
              ...previous,
              coupons: Array.isArray(couponResult?.coupons)
                ? couponResult.coupons
                : [],
            }));
          } catch (error) {
            console.warn("Coupon API failed on cached checkout:", error);
          }

          return {
            success: true,
            cached: true,
            ...savedCheckout,
          };
        }
      }

      resetCartService();

      const orderResult = await generateOrderNumber({
        userid: restaurantUserId,
        custMobile: customerMobile,
        orderType: finalOrderType,
        platform: "WEB",
      });

      const generatedOrderId = orderResult?.orderId;

      if (!generatedOrderId) {
        throw new Error("Order number was not generated.");
      }

      const addToCartResult = await addItemsToServerCart({
        orderId: generatedOrderId,
        userid: restaurantUserId,
        custMobile: customerMobile,
        custName: customerName || "Customer",
        items,
        currency: "INR",
      });

      const calculationResult = await calculateDeliveryChargesAndDiscount({
        orderId: generatedOrderId,
        orderType: finalOrderType,
      });

      const cartDetailsResult = await getCartDetails({
        orderId: generatedOrderId,
        orderType: finalOrderType,
      });

      const couponAmount = Number(
        calculationResult?.calculation?.payAmount ??
          calculationResult?.calculation?.orderAmount ??
          subtotal ??
          0,
      );

      const optionalResults = await Promise.allSettled([
        getCatalogItemsSuggestions({
          userid: restaurantUserId,
        }),

        getCouponCodes({
          amount: couponAmount,
          orderId: generatedOrderId,
          orderType: finalOrderType,
          userid: restaurantUserId,
        }),
      ]);

      const suggestionsResult =
        optionalResults[0]?.status === "fulfilled"
          ? optionalResults[0].value
          : {
              suggestions: [],
              response: null,
            };

      const couponsResult =
        optionalResults[1]?.status === "fulfilled"
          ? optionalResults[1].value
          : {
              coupons: [],
              response: null,
            };

      if (optionalResults[0]?.status === "rejected") {
        console.warn("Suggestions API failed:", optionalResults[0].reason);
      }

      if (optionalResults[1]?.status === "rejected") {
        console.warn("Coupon API failed:", optionalResults[1].reason);
      }

      const preparedCheckout = {
        orderId: generatedOrderId,
        orderType: finalOrderType,

        restaurantName:
          orderResult?.restaurantName ||
          restaurant?.restaurant_name ||
          restaurant?.shop_name ||
          "",

        calculation: calculationResult?.calculation || null,

        cart: cartDetailsResult?.cart || null,

        cartDetails: Array.isArray(cartDetailsResult?.cartDetails)
          ? cartDetailsResult.cartDetails
          : [],

        suggestions: Array.isArray(suggestionsResult?.suggestions)
          ? suggestionsResult.suggestions
          : [],

        coupons: Array.isArray(couponsResult?.coupons)
          ? couponsResult.coupons
          : [],

        addedItems: addToCartResult?.items || [],
      };

      setCheckoutApiData(preparedCheckout);

      saveCheckoutResult(finalCheckoutSignature, preparedCheckout);

      return {
        success: true,
        ...preparedCheckout,
      };
    } catch (error) {
      console.error("Checkout preparation failed:", {
        error,
        message: error?.message,
        restaurantUserId,
        customerMobile,
        orderType: finalOrderType,
        items,
      });

      const message =
        error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to prepare checkout.";

      setLocalCheckoutError(message);

      return {
        success: false,
        message,
        error,
      };
    } finally {
      setCheckoutPreparing(false);
    }
  },
  [
    restaurantUserId,
    restaurant,
    customerMobile,
    customerName,
    items,
    generateOrderNumber,
    addItemsToServerCart,
    calculateDeliveryChargesAndDiscount,
    getCartDetails,
    getCatalogItemsSuggestions,
    getCouponCodes,
    resetCartService,
  ],
);

  const placeOrderProgressText = useMemo(() => {
    switch (currentStep) {
      case "place_order":
      case "update_order_type":
      case "placing_order":
        return "Placing your order...";

      case "generate_payment_intent":
        return "Generating payment QR...";

      case "order_completed":
        return "Order prepared successfully";

      default:
        return "Processing your order...";
    }
  }, [currentStep]);

useEffect(() => {
  // =====================================================
  // ADDRESS RETURNED FROM SAVED ADDRESS PAGE
  // =====================================================

  if (location.state?.selectedAddress) {
    return;
  }

  const selectedAddress = getAddressText();

  if (!selectedAddress) {
    setAddress("");
    setSelectedAddressDetails(null);
    setAddressLoading(false);

    return;
  }

  let parsedAddressDetails = null;

  try {
    const storedDetails =
      localStorage.getItem("selectedDeliveryAddressDetails") ||
      sessionStorage.getItem("selectedDeliveryAddressDetails");

    parsedAddressDetails = storedDetails ? JSON.parse(storedDetails) : null;
  } catch {
    parsedAddressDetails = null;
  }

  setAddress(selectedAddress);

  setSelectedAddressDetails({
    ...(parsedAddressDetails || {}),
    address: selectedAddress,

    favourite:
      parsedAddressDetails?.favourite ||
      parsedAddressDetails?.tag ||
      parsedAddressDetails?.label ||
      "Home",

    source: "saved-address",
  });

  setAddressLoading(false);
}, [location.state?.selectedAddress]);

const cartSignature = useMemo(
  () =>
    JSON.stringify(
      items.map((item) => ({
        itemId: item?.itemId,
        sizeId: item?.sizeId || item?.size,
        quantity: item?.quantity,
        unitPrice: item?.unitPrice || item?.itemPrice,
        customization: item?.customization || "",
      })),
    ),
  [items],
);

useEffect(() => {
  if (items.length === 0 || !restaurantUserId || !customerMobile) {
    return;
  }

  const prepareCheckout = async () => {
    // First checkout load
    if (!checkoutStartedRef.current) {
      checkoutStartedRef.current = true;

      await startCheckoutProcess();
      return;
    }

    // Cart changed after checkout was already prepared
    // Keep the existing checkout UI visible.
    // subtotal will update immediately from CartContext.

    await startCheckoutProcess({
      force: true,
    });
  };

  prepareCheckout();
}, [cartSignature, restaurantUserId, customerMobile, startCheckoutProcess]);


 useEffect(() => {
   if (!location.state?.selectedAddress) return;

   const selected = location.state.selectedAddress;

   setAddress(selected.address);
   setSelectedAddressDetails(selected);

   navigate(location.pathname + location.search, {
     replace: true,
     state: {},
   });
 }, [location.state, navigate, location.pathname, location.search]);


const serverCalculation = checkoutApiData?.calculation || {};

const serverItemTotal = subtotal;

const deliveryCharge = getNumber(serverCalculation?.deliveryFee, 0);

const discountAmount = getNumber(serverCalculation?.discount, 0);

const couponDiscount = getNumber(serverCalculation?.couponCodeDiscount, 0);

const loyaltyDiscount = getNumber(serverCalculation?.loyaltyPointsOff, 0);

const packagingCharge = getNumber(serverCalculation?.packagingCharge, 0);

const grandTotal = Math.max(
  0,
  serverItemTotal +
    deliveryCharge +
    packagingCharge -
    discountAmount -
    couponDiscount -
    loyaltyDiscount,
);

  const availableCoupons = Array.isArray(checkoutApiData?.coupons)
    ? checkoutApiData.coupons
    : [];

  const restaurantDisplayName =
    checkoutApiData?.restaurantName ||
    restaurant?.restaurant_name ||
    restaurant?.shop_name ||
    "Your order";

  const visibleCheckoutError =
    localCheckoutError || placeOrderError || cartError;

    const handleCouponChange = async (couponCode) => {
      setCoupon(couponCode);

      if (!couponCode) {
        return;
      }

      const currentOrderId = String(checkoutApiData?.orderId || "").trim();

      if (!currentOrderId) {
        setLocalCheckoutError("Order ID is missing.");
        return;
      }

      try {
        setRecalculating(true);
        setLocalCheckoutError("");

        // 1. Apply coupon
        const applyResult = await applyCouponCode({
          couponCode,
          orderid: currentOrderId,
        });

        if (!applyResult?.success) {
          throw new Error(applyResult?.message || "Unable to apply coupon.");
        }

        const calculationResult = await calculateCouponTotal({
          orderId: currentOrderId,
          orderType: "home_delivery",
        });

        if (!calculationResult?.success) {
          throw new Error(
            calculationResult?.message || "Unable to calculate updated amount.",
          );
        }

        // 3. Update checkout UI immediately
        setCheckoutApiData((previous) => {
          if (!previous) {
            return previous;
          }

          return {
            ...previous,
            calculation: calculationResult.calculation,
          };
        });

        console.log(
          "Coupon applied successfully:",
          calculationResult.calculation,
        );
      } catch (error) {
        console.error("Coupon apply error:", error);

        setLocalCheckoutError(error?.message || "Unable to apply coupon.");
      } finally {
        setRecalculating(false);
      }
    };

  const latitude = user?.latitude || user?.lat || "";
  const longitude = user?.longitude || user?.lng || "";

  const handleAddressSelect = (selectedAddress) => {
    const receivedAddress = String(selectedAddress?.address || "").trim();

    if (!receivedAddress) {
      setLocalCheckoutError("Selected delivery address is not available.");

      return;
    }

    setAddress(receivedAddress);

    setSelectedAddressDetails({
      ...selectedAddress,
      address: receivedAddress,
    });

    setLocalCheckoutError("");

    try {
      window.localStorage.setItem("chaafo-checkout-address", receivedAddress);
    } catch (error) {
      console.error("Unable to save selected address:", error);
    }

    setAddressModalOpen(false);
  };

const handleAddMoreItems = () => {
  const currentRestaurantUserId = getRestaurantUserId(restaurant);

  if (!currentRestaurantUserId) {
    console.error("Restaurant user ID missing:", restaurant);

    setLocalCheckoutError("Unable to open restaurant.");

    return;
  }

  navigate(`/restaurant-details/${currentRestaurantUserId}`, {
    state: {
      restaurant,

      orderType: "home_delivery",

      platform: "WEB",

      selectedItemName: "",
    },
  });
};

  const handlePlaceOrder = async () => {
    if (placingOrder || orderPlaced) {
      return;
    }

    const currentOrderId = String(checkoutApiData?.orderId || "").trim();

    if (!currentOrderId) {
      setLocalCheckoutError(
        "Order ID is missing. Please prepare checkout again.",
      );

      return;
    }

    if (!restaurantUserId) {
      setLocalCheckoutError("Restaurant user ID is missing.");

      return;
    }

    if (!customerMobile) {
      setLocalCheckoutError("Customer mobile number is missing.");

      return;
    }

 if (!address) {
   setLocalCheckoutError("Please add a delivery address.");

   return;
 }

    setLocalCheckoutError("");
    setPaymentDetails(null);

    const isOnline = "Y";

 const result = await placeOrderSequentially({
   userid: restaurantUserId,

   orderId: currentOrderId,

   custName: customerName || "Customer",

   custMobile: customerMobile,

   isOnline,

   // FINAL PAYABLE AMOUNT
   charges: grandTotal,

   orderFrom: "WEB",

   is_scheduled: "N",

   scheduled_date: "",

   note: "",
 });

    if (!result?.success) {
      setLocalCheckoutError(result?.message || "Unable to place order.");

      return;
    }

   const paymentLink = String(
     result?.paymentLink ||
       result?.payment?.data?.paymentLink ||
       result?.payment?.data?.link ||
       "",
   ).trim();

    if (!paymentLink) {
      setLocalCheckoutError(
        "Order was placed, but payment URL was not received.",
      );

      return;
    }

  setPaymentDetails({
    link: paymentLink,
    paymentLink,

    orderId: currentOrderId,

    amount: grandTotal,

    userid: restaurantUserId,

    custMobile: customerMobile,

    orderType: "home_delivery",

    clientName: restaurantDisplayName,
  });

    setOrderPlaced(true);
    setPaymentModalOpen(true);

    try {
      window.localStorage.removeItem(CHECKOUT_RESULT_KEY);

      window.localStorage.removeItem(CHECKOUT_SIGNATURE_KEY);
    } catch (error) {
      console.error("Unable to clear checkout cache:", error);
    }
  };

  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (items.length === 0) {
    return (
      <main className="min-h-screen bg-blue-50 px-4 py-10">
        <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center rounded-[28px] border border-blue-100 bg-white p-8 text-center shadow-[0_18px_45px_rgba(30,64,175,0.10)]">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-blue-700">
            <ShoppingBag size={34} />
          </div>

          <h1 className="mt-5 text-2xl font-bold text-slate-900">
            Your cart is empty
          </h1>

          <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
            Add items from the restaurant menu before opening checkout.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-6 h-12 rounded-xl bg-blue-700 px-6 text-sm font-bold text-white transition hover:bg-blue-800"
          >
            Back to menu
          </button>
        </div>
      </main>
    );
  }

  /* =====================================================
     CHECKOUT LOADING
  ===================================================== */

  if ((checkoutPreparing || cartLoading) && !checkoutApiData) {
    const loadingMessages = {
      starting: "Preparing checkout...",

      generate_order_number: "Generating order number...",

      add_to_cart: "Adding items to your cart...",

      calculate_delivery_charges_and_discount:
        "Calculating delivery charges and discounts...",

      get_cart_details: "Fetching updated cart details...",

      get_catalogue_suggestions: "Loading item suggestions...",

      get_coupon_codes: "Loading available coupons...",
    };

    return (
      <main className="flex min-h-screen items-center justify-center bg-blue-50 px-4">
        <div className="w-full max-w-md rounded-[24px] border border-blue-100 bg-white p-8 text-center shadow-[0_18px_45px_rgba(30,64,175,0.12)]">
          <div className="mx-auto h-11 w-11 animate-spin rounded-full border-4 border-blue-100 border-t-blue-700" />

          <h1 className="mt-5 text-xl font-bold text-slate-900">
            Preparing your checkout
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            {loadingMessages[currentStep] || "Please wait..."}
          </p>
        </div>
      </main>
    );
  }

  /* =====================================================
     CHECKOUT ERROR
  ===================================================== */

  if (visibleCheckoutError && !checkoutApiData) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-blue-50 px-4">
        <div className="w-full max-w-md rounded-[24px] border border-red-100 bg-white p-8 text-center shadow-[0_18px_45px_rgba(30,64,175,0.12)]">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
            <X size={28} />
          </div>

          <h1 className="mt-5 text-xl font-bold text-slate-900">
            Checkout could not be prepared
          </h1>

          <p className="mt-3 text-sm leading-6 text-red-600">
            {visibleCheckoutError}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-12 rounded-xl border border-blue-200 text-sm font-bold text-blue-700 transition hover:bg-blue-50"
            >
              Back
            </button>

            <button
              type="button"
              onClick={() => {
                try {
                  window.localStorage.removeItem(CHECKOUT_RESULT_KEY);

                  window.localStorage.removeItem(CHECKOUT_SIGNATURE_KEY);
                } catch {}

                setCheckoutApiData(null);
                setLocalCheckoutError("");

                startCheckoutProcess({
                  force: true,
                });
              }}
              className="flex h-12 items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 text-sm font-bold text-white transition hover:bg-blue-800"
            >
              <RefreshCw size={17} />
              Try Again
            </button>
          </div>
        </div>
      </main>
    );
  }

  /* =====================================================
     MAIN UI
  ===================================================== */

  return (
    <main className="min-h-screen bg-[#f5f8ff] pb-28 lg:pb-12">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-blue-100 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex h-[68px] max-w-[1280px] items-center gap-3 px-4 sm:px-6 lg:h-[76px] lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
            aria-label="Back"
          >
            <ArrowLeft size={21} />
          </button>

          <div className="min-w-0 flex-1">
            <h1 className="text-[19px] font-bold text-slate-900 sm:text-[22px]">
              Checkout
            </h1>

            <p className="truncate text-xs text-slate-500 sm:text-sm">
              {restaurantDisplayName}

              {checkoutApiData?.orderId
                ? ` • Order #${checkoutApiData.orderId}`
                : ""}
            </p>
          </div>

          <div className="hidden rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 sm:block">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1280px] gap-6 px-3 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_390px] lg:items-start lg:px-8 lg:py-8 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="min-w-0 space-y-4 sm:space-y-5">
          {/* Inline error */}
          {visibleCheckoutError && (
            <div className="flex items-start justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm leading-6 text-red-700">
                {visibleCheckoutError}
              </p>

              <button
                type="button"
                onClick={() => setLocalCheckoutError("")}
                className="shrink-0 text-red-500"
              >
                <X size={18} />
              </button>
            </div>
          )}

          {/* Cart */}
          <SectionCard
            title="Your Cart"
            action={
              <div className="flex items-center gap-2">
                {/* ADD MORE ITEM */}
                <button
                  type="button"
                  onClick={handleAddMoreItems}
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 text-xs font-bold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                >
                  <Plus size={14} strokeWidth={2.5} />
                  Add Item
                </button>

                {/* CLEAR CART */}
                <button
                  type="button"
                  onClick={clearCart}
                  className="text-sm font-medium text-slate-400 transition hover:text-blue-700"
                >
                  All Clear
                </button>
              </div>
            }
          >
            <div className="-my-3">
             {items.map((item) => {
  const customizationKey =
    typeof item?.customization === "string"
      ? item.customization
      : JSON.stringify(
          item?.selectedCustomizations ||
            item?.customization ||
            {},
        );

  const cartRowKey = [
    item?.restaurantId || "",
    item?.itemId || "",
    item?.sizeId || item?.size || "Regular",
    customizationKey,
  ].join("::");

  return (
    <CartItem
      key={cartRowKey}
      item={item}
      increaseItem={increaseItem}
      decreaseItem={decreaseItem}
      removeItem={removeItem}
    />
  );
})}
            </div>
          </SectionCard>

          {/* Delivery */}
          <SectionCard
            title="Delivery Address"
            action={
              recalculating ? (
                <span className="flex items-center gap-2 text-xs font-semibold text-blue-700">
                  <RefreshCw size={14} className="animate-spin" />
                  Updating
                </span>
              ) : null
            }
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Deliver To
              </p>

              <button
                type="button"
                onClick={handleOpenSavedAddresses}
                className="text-sm font-bold text-blue-700 transition hover:text-blue-800"
              >
                {address ? "Change" : "Add"}
              </button>
            </div>

            <div className="mt-3 rounded-2xl border border-blue-100 bg-blue-50/50 p-4 sm:p-5">
              {address && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-3 py-1.5 text-xs font-bold text-white">
                  <Home size={15} />

                  {selectedAddressDetails?.favourite ||
                    selectedAddressDetails?.tag ||
                    selectedAddressDetails?.label ||
                    "Home"}
                </div>
              )}

              {addressLoading ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                  <RefreshCw size={16} className="animate-spin" />
                  Loading delivery address...
                </div>
              ) : (
                <p className="mt-4 break-words text-sm leading-6 text-slate-700">
                  {address || "Add a delivery address to place your order."}
                </p>
              )}
            </div>
          </SectionCard>

          {/* Coupons */}
          <SectionCard title="Coupon">
            {availableCoupons.length > 0 ? (
              <div className="relative">
                <select
                  value={coupon}
                  onChange={(event) => {
                    handleCouponChange(event.target.value);
                  }}
                  className="h-12 w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 pr-11 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                >
                  <option value="">Select a coupon</option>

                  {availableCoupons.map((couponItem) => (
                    <option
                      key={couponItem?.id || couponItem?.coupon_code}
                      value={couponItem?.coupon_code || ""}
                    >
                      {couponItem?.coupon_code}

                      {" - "}

                      {couponItem?.coupon_title ||
                        couponItem?.coupon_description ||
                        `${couponItem?.discount || 0} ${couponItem?.discount_type || "discount"}`}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
                />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center">
                <p className="text-sm text-slate-500">
                  No coupons are currently available.
                </p>
              </div>
            )}

            {coupon && (
              <p className="mt-3 text-xs font-medium text-blue-700">
                Selected coupon: {coupon}
              </p>
            )}
          </SectionCard>

          {/* Payment */}
          <SectionCard title="Payment Method">
            <div className="flex min-h-16 items-center justify-between rounded-2xl border border-blue-600 bg-blue-50 px-4 sm:px-5">
              <div>
                <p className="text-sm font-bold text-blue-900">Pay Online</p>

                <p className="mt-1 text-xs text-blue-700">
                  Pay securely using UPI or QR code
                </p>
              </div>

              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-700 text-white">
                <Check size={14} />
              </span>
            </div>
          </SectionCard>

          {/* Mobile bill */}
          <SectionCard title="Bill Summary" className="lg:hidden">
            <div className="space-y-3">
              <BillRow
                label="Item Total"
                value={formatCurrency(serverItemTotal)}
              />

              <BillRow
                label="Delivery Charges"
                value={formatCurrency(deliveryCharge)}
              />

              {packagingCharge > 0 && (
                <BillRow
                  label="Packaging Charges"
                  value={formatCurrency(packagingCharge)}
                />
              )}

              {discountAmount > 0 && (
                <BillRow
                  label="Discount"
                  value={`- ${formatCurrency(discountAmount)}`}
                  positive
                />
              )}

              {couponDiscount > 0 && (
                <BillRow
                  label="Coupon Discount"
                  value={`- ${formatCurrency(couponDiscount)}`}
                  positive
                />
              )}

              {loyaltyDiscount > 0 && (
                <BillRow
                  label="Loyalty Discount"
                  value={`- ${formatCurrency(loyaltyDiscount)}`}
                  positive
                />
              )}

              <div className="border-t border-dashed border-slate-200 pt-3">
                <BillRow
                  label="To Pay"
                  value={formatCurrency(grandTotal)}
                  strong
                />
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Desktop bill */}
        <aside className="sticky top-[100px] hidden lg:block">
          <div className="rounded-[28px] border border-blue-100 bg-white p-6 shadow-[0_18px_45px_rgba(30,64,175,0.12)]">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                  Order Summary
                </p>

                <h2 className="mt-2 text-xl font-bold text-slate-900">
                  {restaurantDisplayName}
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  {itemCount} {itemCount === 1 ? "item" : "items"}
                  {checkoutApiData?.orderId
                    ? ` • #${checkoutApiData.orderId}`
                    : ""}
                </p>
              </div>

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                <ShoppingBag size={23} />
              </div>
            </div>

            <div className="space-y-3 py-5">
              <BillRow
                label="Item Total"
                value={formatCurrency(serverItemTotal)}
              />

              <BillRow
                label="Delivery Charges"
                value={formatCurrency(deliveryCharge)}
              />

              {packagingCharge > 0 && (
                <BillRow
                  label="Packaging Charges"
                  value={formatCurrency(packagingCharge)}
                />
              )}

              {discountAmount > 0 && (
                <BillRow
                  label="Discount"
                  value={`- ${formatCurrency(discountAmount)}`}
                  positive
                />
              )}

              {couponDiscount > 0 && (
                <BillRow
                  label="Coupon Discount"
                  value={`- ${formatCurrency(couponDiscount)}`}
                  positive
                />
              )}

              {loyaltyDiscount > 0 && (
                <BillRow
                  label="Loyalty Discount"
                  value={`- ${formatCurrency(loyaltyDiscount)}`}
                  positive
                />
              )}
            </div>

            <div className="border-t border-dashed border-slate-200 pt-5">
              <BillRow
                label="To Pay"
                value={formatCurrency(grandTotal)}
                strong
              />
            </div>

            <button
              type="button"
              onClick={handlePlaceOrder}
              disabled={
                recalculating ||
                placingOrder ||
                orderPlaced ||
                !checkoutApiData?.orderId ||
                !address
              }
              className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 text-base font-bold text-white shadow-[0_12px_28px_rgba(29,78,216,0.30)] transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
            >
              {placingOrder ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />

                  {placeOrderProgressText}
                </>
              ) : orderPlaced ? (
                <>
                  <Check size={19} />
                  Payment QR Ready
                </>
              ) : (
                <>Place Order · {formatCurrency(grandTotal)}</>
              )}
            </button>

            {!address && (
              <p className="mt-3 text-center text-xs text-red-500">
                Please add a delivery address.
              </p>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile fixed button */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-blue-100 bg-white p-3 pb-[max(12px,env(safe-area-inset-bottom))] lg:hidden">
        <button
          type="button"
          onClick={handlePlaceOrder}
          disabled={
            recalculating ||
            placingOrder ||
            orderPlaced ||
            !checkoutApiData?.orderId ||
            !address
          }
          className="flex h-[60px] w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 text-base font-bold text-white shadow-[0_12px_28px_rgba(29,78,216,0.28)] transition disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
        >
          {placingOrder ? (
            <>
              <RefreshCw size={18} className="animate-spin" />
              Placing Order...
            </>
          ) : orderPlaced ? (
            <>
              <Check size={19} />
              Order Placed
            </>
          ) : (
            <>Place Order · {formatCurrency(grandTotal)}</>
          )}
        </button>
      </div>

      {/* Address modal */}
      <AddressModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        customerId={customerId}
        customerMobile={customerMobile}
        restaurantUserId={restaurantUserId}
        selectedAddress={address}
        onAddressSelect={handleAddressSelect}
      />

      {paymentModalOpen && paymentDetails && (
        <div
          className="
        fixed
        inset-0
        z-[3100]
        flex
        items-center
        justify-center
        bg-slate-950/70
        backdrop-blur-sm

        sm:p-5
      "
        >
          {/* BACKDROP */}

          <button
            type="button"
            aria-label="Close payment modal"
            onClick={() => {
              setPaymentModalOpen(false);
            }}
            className="absolute inset-0"
          />

          {/* ============================================= */}
          {/* EXISTING PAYMENT QR UI */}
          {/* ============================================= */}

          <div
            className="
          relative
          z-10

          h-[100dvh]
          w-full

          overflow-hidden

          sm:h-[94dvh]
          sm:max-w-[500px]
          sm:rounded-[28px]
        "
          >
            {/* CLOSE */}

            <button
              type="button"
              onClick={() => {
                setPaymentModalOpen(false);
              }}
              className="
            absolute
            right-3
            top-3
            z-50

            flex
            h-9
            w-9
            items-center
            justify-center

            rounded-full
            bg-white/15
            text-white
            backdrop-blur-md

            transition
            hover:bg-white/25
          "
              aria-label="Close payment"
            >
              <X size={18} />
            </button>

            <PaymentQr
              embedded
              flow="chaafo"
              paymentLink={paymentDetails.paymentLink || paymentDetails.link}
              orderId={paymentDetails.orderId}
              userid={paymentDetails.userid}
              custMobile={paymentDetails.custMobile}
              orderType={paymentDetails.orderType}
              amount={paymentDetails.amount}
              clientName={paymentDetails.clientName}
              onPaymentSuccess={async (paidOrderId) => {
                try {
                  console.log("✅ PAYMENT SUCCESS:", paidOrderId);

                  await getOrderReceiptDetails({
                    orderid: paidOrderId,
                    platform: "WEB",
                  });

                  setPaymentModalOpen(false);

                  navigate(`/order-summary?orderid=${paidOrderId}&download=1`);
                } catch (error) {
                  console.error("❌ Receipt API failed:", error);
                }
              }}
            />
          </div>
        </div>
      )}
    </main>
  );
}
